'use client'

import React from 'react'
import { Link } from '@/lib'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import Components from '../components'
import { categoryIcons } from '@/components/CreativeCategoryIcons'

interface CreativeProject {
  title: string
  description: string
  category: string
  year?: string
  tags: string[]
  image?: string
  featured?: boolean
  link?: string
}

const creativeProjects: CreativeProject[] = [
  {
    title: 'Volunteer & Community Design Work',
    description: 'Pro bono design projects for local nonprofits and community organizations, personal graphic and visual design projects, experimental typography, and visual communication work',
    category: 'Volunteer & Community Design',
    year: '2019-2025',
    tags: [],
    image: '/assets/creative-projects/Midway-T-Shirt-Actual.png',
    featured: false,
    link: '/VolunteerCommunityDesign'
  },
  {
    title: 'Painting',
    description: 'Traditional and digital painting explorations exploring color, composition, and expressive mark-making',
    category: 'Painting',
    year: '2009-2020',
    tags: [],
    image: '/assets/creative-projects/ArchesNP.jpg',
    featured: false,
    link: '/Painting'
  },
  {
    title: 'Drawing & Illustration',
    description: 'Pen and ink drawings, character illustrations, and narrative visual storytelling through line and form',
    category: 'Drawing & Illustration',
    year: undefined,
    tags: [],
    image: '/assets/creative-projects/Birdcollage.JPG',
    featured: false,
    link: '/DrawingIllustration'
  },
  {
    title: 'Writing',
    description: 'Creative writing, design thinking essays, and documentation exploring the intersection of design, creativity, and human experience',
    category: 'Writing',
    year: undefined,
    tags: [],
    image: '/assets/creative-projects/Back Cover - Final (ebook).png',
    featured: false,
    link: '/Writing'
  }
]


export default function CreativeProjects() {
  return (
    <Components.PageContainer>
      <Components.SkipLink />
      <Components.GlassNavigation />
      
      <main id="main-content" tabIndex={-1} className="pt-16 md:pt-32 pb-16 px-4 md:px-6" role="main" aria-label="Creative projects">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <header className="text-center mb-12 md:mb-20">
            <h1 className="text-3xl md:text-4xl lg:text-6xl font-bold text-[var(--brutal-ink)] mb-4 md:mb-6 leading-tight break-words px-4">
              Creative Projects
            </h1>
            <p className="text-base md:text-lg lg:text-xl text-[var(--brutal-muted)] max-w-3xl mx-auto leading-relaxed break-words px-4">
              Personal creative explorations, volunteer work, and passion-driven design projects
            </p>
          </header>

          {/* Projects Grid - Two column layout with image cards */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6" aria-label="Creative projects gallery">
            {creativeProjects.map((project, index) => {
              const CardContent = (
                <>
                  {/* Image Background */}
                  <div className="absolute inset-0">
                    {project.image ? (
                      <img 
                        src={project.image} 
                        alt={project.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    ) : (
                      <div className="w-full h-full bg-[var(--brutal-kraft)]" />
                    )}
                    {/* Dark overlay for text readability */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-black/40" />
                  </div>

                  {/* Text Overlay — explicit white for contrast on dark media */}
                  <div className="relative h-full flex flex-col justify-end p-6 md:p-8 z-10 text-white [&_h3]:!text-white [&_p]:!text-white/90">
                    <div className="flex items-center gap-2 mb-4 flex-shrink-0">
                      <div className="text-[var(--brutal-mustard)]">
                        {categoryIcons[project.category] || categoryIcons['Branding']}
                      </div>
                      <Badge 
                        variant="secondary" 
                        className="bg-[var(--brutal-ink)]/50 !text-white border border-white/50 text-xs backdrop-blur-sm"
                      >
                        {project.category}
                      </Badge>
                      {project.featured && (
                        <Badge className="brutal-btn !text-[var(--brutal-ink)] border-0 text-xs">
                          Featured
                        </Badge>
                      )}
                    </div>

                    <h3 id={`creative-project-${index}-title`} className="text-2xl md:text-3xl font-bold !text-white mb-3 group-hover:!text-[var(--brutal-mustard)] transition-colors duration-200 leading-tight break-words flex-shrink-0">
                      {project.title}
                    </h3>

                    <p className="!text-white/90 text-sm md:text-base leading-relaxed mb-4 flex-shrink-0 break-words">
                      {project.description}
                    </p>

                    {project.year && (
                      <div className="!text-white/75 text-xs font-medium flex-shrink-0">
                        {project.year}
                      </div>
                    )}
                  </div>
                </>
              )

              return project.link ? (
                <Link key={index} to={project.link} className="block h-full">
                  <Card
                    role="article"
                    variant="media"
                    className={`
                      relative overflow-hidden
                      brutal-panel brutal-media
                      hover:scale-[1.02] hover:shadow-[6px_6px_0_#141414] cursor-pointer
                      h-full min-h-[400px] md:min-h-[500px]
                      group
                      !p-0
                    `}
                    aria-labelledby={`creative-project-${index}-title`}
                  >
                    {CardContent}
                  </Card>
                </Link>
              ) : (
                <Card
                  key={index}
                  role="article"
                  variant="media"
                  className={`
                    relative overflow-hidden
                    brutal-panel brutal-media
                    hover:scale-[1.02] hover:shadow-[6px_6px_0_#141414] cursor-pointer
                    h-full min-h-[400px] md:min-h-[500px]
                    group
                    !p-0
                  `}
                  aria-labelledby={`creative-project-${index}-title`}
                >
                  {CardContent}
                </Card>
              )
            })}
          </section>

          {/* Call to Action */}
          <section className="mt-16 md:mt-20 text-center" aria-labelledby="collaboration-heading">
            <Card className="backdrop-blur-xl bg-[var(--brutal-kraft)] border border-pink-400/30 p-8 md:p-12 max-w-2xl mx-auto">
              <h2 id="collaboration-heading" className="text-2xl md:text-3xl font-bold text-[var(--brutal-ink)] mb-4 break-words">
                Interested in Collaborating?
              </h2>
              <p className="text-[var(--brutal-muted)] text-base md:text-lg mb-6 break-words">
                I'm always open to creative collaborations, volunteer opportunities, and passion projects that make a positive impact.
              </p>
              <Button
                asChild
                className="bg-gradient-to-r from-[var(--brutal-accent)] to-[var(--brutal-mustard)] hover:from-[var(--brutal-accent)] hover:to-[var(--brutal-mustard)] text-[var(--brutal-ink)] font-medium"
              >
                <Link to="/Contact">
                  Get in Touch
                </Link>
              </Button>
            </Card>
          </section>
        </div>
      </main>
    </Components.PageContainer>
  )
}

